import React, { useEffect, useMemo, useState } from "react";
import { CommonLayout } from "../../layouts/CommonLayout/CommonLayout";
import Typography from "@material-ui/core/Typography";
import { Menu, MenuItem, Paper } from "@material-ui/core";
import DocumentsTreeView from "../../components/DocumentsTreeView/DocumentsTreeView.jsx";
import { makeStyles } from "@material-ui/styles";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined.js";
import ChromeReaderModeIcon from "@material-ui/icons/ChromeReaderMode";
import BusinessIcon from "@material-ui/icons/Business";


import { useDispatch, useSelector } from "react-redux";
import {
  copyProject,
  createCompany,
  createContract,
  createProject,
  deleteElement,
  getCompaniesNodes,
  moveProjectTreeElement,
  selectCompanies,
  selectCompaniesNodes,
  selectContracts,
  selectLoading,
  selectProjects,
  updateCompany,
  updateContract,
  updateProject,
} from "../../redux/reducers/projects-edit-reducer.js";
import { useDialog } from "../../hooks/useDialog.js";
import AddCompanyDialog from "../../components/ProjectsTreeDialogs/AddCompanyDialog.jsx";
import DeleteDialog from "../../components/DeleteDialog/DeleteDialog.jsx";
import AddContractDialog from "../../components/ProjectsTreeDialogs/AddContractDialog.jsx";
import AddProjectDialog from "../../components/ProjectsTreeDialogs/AddProjectDialog.jsx";
import EditCompanyDialog from "../../components/ProjectsTreeDialogs/EditCompanyDialog.jsx";
import EditContractDialog from "../../components/ProjectsTreeDialogs/EditContractDialog.jsx";
import EditProjectDialog from "../../components/ProjectsTreeDialogs/EditProjectDialog.jsx";
import { createInfoNotification } from "../../redux/reducers/notification-reducer.js";
import { useRedirect } from "../../hooks/useRedirect.js";
import { RouteNames } from "../../router/consts.js";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(1),
    width: "100%",
  },
  header: {
    paddingBottom: theme.spacing(1),
  },
}));

const AddMenu = (props) => {
  const {
    anchorEl,
    handleClose,
    parentNode,
    onAddCompanyButtonClick,
    onAddContractButtonClick,
    onAddProjectButtonClick,
  } = props;

  const companies = useSelector(selectCompanies);
  const contracts = useSelector(selectContracts);

  const getTitle = (parentNode, companies, contracts) => {
    const [type, id] = parentNode?.split(".") || [];
    if (type === "company")
      return (
        companies.find((company) => company.id === parseInt(id))?.name ||
        "корень"
      );
    if (type === "contract")
      return (
        contracts.find((contract) => contract.id === parseInt(id))?.name ||
        "корень"
      );
    return "корень";
  };

  const title = useMemo(
    () => getTitle(parentNode, companies, contracts),
    [parentNode, companies, contracts]
  );

  const handleAddCompanyButtonClick = () => {
    handleClose();
    onAddCompanyButtonClick();
  };

  const handleAddContractButtonClick = () => {
    handleClose();
    onAddContractButtonClick();
  };

  const handleAddProjectButtonClick = () => {
    handleClose();
    onAddProjectButtonClick();
  };

  return (
    <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
      <MenuItem
        onClick={handleAddCompanyButtonClick}
        disabled={parentNode?.includes("contract")}
      >
        Добавить в {title}
      </MenuItem>
      <MenuItem
        onClick={handleAddContractButtonClick}
        disabled={parentNode?.includes("contract") || parentNode === null}
      >
        Добавить договор в {title}
      </MenuItem>
      <MenuItem
        onClick={handleAddProjectButtonClick}
        disabled={!parentNode?.includes("contract")}
      >
        Добавить тип акта в {title}
      </MenuItem>
    </Menu>
  );
};

const ProjectsPage = () => {
  const classes = useStyles();

  const companiesNodes = useSelector(selectCompaniesNodes);
  const companies = useSelector(selectCompanies);
  const contracts = useSelector(selectContracts);
  const projects = useSelector(selectProjects);
  const loading = useSelector(selectLoading);

  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [parentNode, setParentNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editNodeID, setEditNodeID] = useState(null);
  const [cutNode, setCutNode] = useState("");

  const getNodeID = (node) => {
    return Number(node?.split(".")[1] || null);
  };

  const parentID = useMemo(() => getNodeID(parentNode), [parentNode]);

  const addCompanyDialog = useDialog();
  const addContractDialog = useDialog();
  const addProjectDialog = useDialog();
  const editCompanyDialog = useDialog();
  const editContractDialog = useDialog();
  const editProjectDialog = useDialog();
  const deleteDialog = useDialog();

  const { redirect } = useRedirect();

  const dispatch = useDispatch();

  const typeIcons = {
    company: BusinessIcon,
    contract: ChromeReaderModeIcon,
    project: DescriptionOutlinedIcon,
  };

  const iconTooltips = {
    company: "Компания",
    contract: "Договор",
    project: "Тип акта",
  };

  const getParentNode = (selectedNode) => {
    const [type, id] = selectedNode.split(".");
    if (id === undefined) return null;
    if (type === "project") {
      return `contract.${
        projects.find((project) => project.id === parseInt(id)).contract
      }`;
    }
    return selectedNode;
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const toolbarCallbacks = {
    onAdd: (nodeID) => async (event) => {
      await setParentNode(getParentNode(nodeID));
      await setAddMenuAnchorEl(event.target);
    },
    onEdit: (nodeID) => async () => {
      const [type, id] = nodeID.split(".");
      await setEditNodeID(parseInt(id));
      if (type === "company") editCompanyDialog.handleOpen();
      if (type === "contract") editContractDialog.handleOpen();
      if (type === "project") editProjectDialog.handleOpen();
    },
    onDelete: (nodeID) => async () => {
      await setSelectedNode(nodeID);
      await deleteDialog.handleOpen();
    },
    onCut: (nodeID) => async () => {
      await setCutNode(nodeID);
      dispatch(createInfoNotification("Вырезано"));
    },
    onPaste: (nodeID) => async () => {
      const result = await dispatch(moveProjectTreeElement(cutNode, nodeID));
      if (result === null) return result;
      await setCutNode("");
      await dispatch(getCompaniesNodes());
    },
    onCopy: (nodeID) => async () => {
      await dispatch(copyProject(getNodeID(nodeID)));
      await dispatch(getCompaniesNodes());
    },
    onDoubleClick: (nodeID) => async () => {
      const [type, id] = nodeID.split(".");
      if (type === "project") {
        redirect(RouteNames.PROJECT_EDIT, { projectID: id });
      }
    },
  };

  useEffect(() => {
    dispatch(getCompaniesNodes());
  }, [dispatch]);

  return (
    <CommonLayout>
      <AddMenu
        anchorEl={addMenuAnchorEl}
        handleClose={handleCloseAddMenu}
        parentNode={parentNode}
        onAddCompanyButtonClick={addCompanyDialog.handleOpen}
        onAddContractButtonClick={addContractDialog.handleOpen}
        onAddProjectButtonClick={addProjectDialog.handleOpen}
      />
      <AddCompanyDialog
        open={addCompanyDialog.open}
        onClose={addCompanyDialog.handleClose}
        submitCallback={async (values) => {
          await dispatch(createCompany({ ...values, company: parentID }));
          await dispatch(getCompaniesNodes());
        }}
      />
      <AddContractDialog
        open={addContractDialog.open}
        onClose={addContractDialog.handleClose}
        submitCallback={async (values) => {
          await dispatch(createContract({ ...values, company: parentID }));
          await dispatch(getCompaniesNodes());
        }}
      />
      <AddProjectDialog
        open={addProjectDialog.open}
        onClose={addProjectDialog.handleClose}
        submitCallback={async (values) => {
          await dispatch(createProject({ ...values, contract: parentID }));
          await dispatch(getCompaniesNodes());
        }}
      />
      <EditCompanyDialog
        open={editCompanyDialog.open}
        onClose={editCompanyDialog.handleClose}
        initialValues={companies.find((company) => company.id === editNodeID)}
        submitCallback={async (values) => {
          await dispatch(updateCompany(values));
          await dispatch(getCompaniesNodes());
        }}
      />
      <EditContractDialog
        open={editContractDialog.open}
        onClose={editContractDialog.handleClose}
        initialValues={contracts.find(
          (contracts) => contracts.id === editNodeID
        )}
        submitCallback={async (values) => {
          await dispatch(updateContract(values));
          await dispatch(getCompaniesNodes());
        }}
      />
      <EditProjectDialog
        open={editProjectDialog.open}
        onClose={editProjectDialog.handleClose}
        initialValues={projects.find((project) => project.id === editNodeID)}
        submitCallback={async (values) => {
          await dispatch(updateProject(values));
          await dispatch(getCompaniesNodes());
        }}
      />
      <DeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        loading={loading}
        submitCallback={async () => {
          await dispatch(deleteElement(selectedNode));
          await dispatch(getCompaniesNodes());
        }}
      />

      <Typography className={classes.header} variant={"h5"}>
        Проекты
      </Typography>
      <Paper className={classes.paper}>
        <DocumentsTreeView
          nodes={companiesNodes}
          typeIcons={typeIcons}
          iconTooltips={iconTooltips}
          isAbleToPaste={Boolean(cutNode)}
          toolbarCallbacks={toolbarCallbacks}
        />
      </Paper>
    </CommonLayout>
  );
};

export default ProjectsPage;
